/**
 * DCS SEO Plugin for Vite
 *
 * Two responsibilities, both driven by `.dcs/seo.yaml`:
 *
 * 1. **Build-time define** (always on): reads `.dcs/seo.yaml` and injects it as
 *    the `__DCS_SEO__` global for the runtime `useSEO` composable.
 *
 * 2. **Static `<head>` emitter** (opt-in via `emitStaticHtml: true`, default
 *    OFF): after the bundle is written, reads the SPA's built `index.html`,
 *    and for every route in `.dcs/pages.yaml` writes a per-route
 *    `dist/<path>/index.html` whose `<head>` carries the resolved title, meta,
 *    canonical, Open Graph, Twitter, and JSON-LD tags. This gives a Vue SPA
 *    per-route static SEO **without** vite-ssg.
 *
 *    VitePress sites already bake SEO via their own config, so they leave this
 *    option OFF and are completely unaffected.
 *
 * @example Runtime define only (default — safe for VitePress)
 * ```typescript
 * // vite.config.ts
 * import { dcsSeoPlugin } from '@duffcloudservices/cms/plugins'
 *
 * export default defineConfig({
 *   plugins: [dcsSeoPlugin({ debug: true })]
 * })
 * ```
 *
 * @example Vue SPA with per-route static <head> emission
 * ```typescript
 * // vite.config.ts
 * import { dcsSeoPlugin } from '@duffcloudservices/cms/plugins'
 *
 * export default defineConfig({
 *   plugins: [
 *     dcsSeoPlugin({
 *       emitStaticHtml: true,        // turn the emitter ON
 *       pagesPath: '.dcs/pages.yaml', // route manifest (default)
 *       noindex: ['account', 'projects'], // robots: noindex,nofollow
 *       exclude: ['/preview'],       // skip these routes entirely
 *     })
 *   ]
 * })
 * ```
 */

import fs from 'node:fs'
import path from 'node:path'
import yaml from 'js-yaml'
import type { Plugin, ResolvedConfig, HtmlTagDescriptor } from 'vite'
import type { SeoConfiguration, SeoHonestyBlock } from '../types/seo'
import { readDcsYamlFresh, buildGlobalAssignScript } from './serveDefineInject'
import { buildHeadTags } from '../seo/headTags'
import { spliceHeadHtml } from '../seo/spliceHeadHtml'
import {
  loadPagesManifest,
  routeExclusionReason,
  isRouteEmitted,
  type PageRouteEntry,
} from '../seo/pagesManifest'
import {
  findDuplicateNormalizedPaths,
  formatDuplicateNormalizedPaths,
} from '../seo/installSeoHead'
import {
  prerenderBodies,
  isBodyPrerenderEnabled,
  routeToOutputFile,
} from '../seo/bodyPrerender'
import { createPlaywrightRenderer } from '../seo/playwrightRenderer'
import {
  assertHeadHonesty,
  assertHeadHonestyExecuted,
  findHeadHonestyViolations,
  resolveHonestyMode,
  type HeadObservation,
  type HonestyMode,
} from '../seo/headHonesty'
import {
  collectEmittedUrlsFromFiles,
  dedupeUrls,
  classifyUrl,
  auditSameOriginAssets,
  checkRemoteAssets,
  formatEmittedUrlReport,
  formatEmittedUrlAudit,
  auditCheckedTotal,
  EmittedUrlError,
  EmittedUrlNotRunError,
  type ClassifiedUrl,
  type EmittedUrlAudit,
  type EmittedUrlViolation,
} from '../seo/emittedUrls'
import {
  hoistCharsetMeta,
  checkCharsetBudget,
  formatCharsetHeadroomWarning,
  CharsetBudgetError,
} from '../seo/charsetBudget'
import {
  buildSitemapXml,
  buildRobotsTxt,
  buildLlmsTxt,
  isHandAuthoredRobotsAcceptable,
  type DcsRobotsOptions,
} from '../seo/sitemap'
import {
  breadcrumbTrailFromRoute,
  findReviewItemsForPage,
  findBusinessLicense,
  findPostalAddressGaps,
  formatPostalAddressGapReport,
  type ContentConfig,
  type ReviewSource,
  type BlogMeta,
  type FaqSource,
} from '../seo/schemaGraph'

export interface DcsSeoPluginOptions {
  /** Path to seo.yaml relative to project root (default: '.dcs/seo.yaml') */
  seoPath?: string
  /** Enable debug logging */
  debug?: boolean

  /**
   * Opt-in: emit per-route static `<head>` (meta + JSON-LD) into the built
   * `dist/` at the end of the build. Default `false` — VitePress sites and any
   * site that bakes its own SEO are unaffected when this is off.
   */
  emitStaticHtml?: boolean

  /**
   * Build-time BODY prerender: after the per-route `<head>` is emitted, render
   * each indexable route's real DOM from the just-built SPA (headless Chromium
   * via the site's `playwright`) and splice it into the mount container so non-JS
   * AI crawlers see the page's body prose — not just `<div id="app"></div>`.
   *
   * Defaults to whatever `emitStaticHtml` is, so every SPA already emitting
   * per-route HTML gains crawler-visible bodies on a cms bump with no per-site
   * edit. Set `false` here to keep head-only emission. It is ALSO overridable
   * per-site (no code change / no cms republish) via `.dcs/seo.yaml`
   * `prerenderBody: false`. noindex/auth-gated routes are never body-prerendered;
   * a route that crashes at render time fails the build loud rather than shipping
   * a broken body. Requires `playwright` in the site (already the fleet default);
   * absent ⇒ graceful no-op (head + JSON-LD still emitted).
   */
  prerenderBody?: boolean

  /**
   * Path to the route manifest relative to project root, used only when
   * `emitStaticHtml` is true. Default `'.dcs/pages.yaml'`.
   */
  pagesPath?: string

  /**
   * Path to `content.yaml` relative to project root, used only when
   * `emitStaticHtml` is true. Threads REAL reviews into the honest
   * Review/aggregateRating on the per-route LocalBusiness node. Default
   * `'.dcs/content.yaml'`. Missing/unparseable ⇒ no reviews emitted.
   */
  contentPath?: string

  /**
   * Routes to skip entirely (no per-route HTML written). Matched against the
   * route `path` (e.g. `'/preview'`) OR the route `slug` (e.g. `'account'`).
   */
  exclude?: string[]

  /**
   * Routes that should receive `robots: noindex, nofollow`. Matched against the
   * route `path` OR `slug`. The home route (`/`) still overwrites
   * `dist/index.html`.
   *
   * This is a PREDICATE OVER `pages.yaml`, not a standalone directive about a
   * URL. An entry that matches no page is a **build error**
   * (`NoindexOrphanError`, C-416) — with no matching page nothing is emitted for
   * it, so the entry would silently do nothing while the URL stayed
   * `index, follow`. Matched raw, so `'/account/'` does not match a `'/account'`
   * route; matched against the full manifest, so an entry that is also in
   * `exclude` is fine.
   */
  noindex?: string[]

  /**
   * Opt-in: emit the site-wide static files `dist/sitemap.xml`,
   * `dist/robots.txt`, and `dist/llms.txt` after the per-route HTML loop.
   *
   * Defaults to whatever `emitStaticHtml` is — so every site already calling
   * `dcsSeoPlugin({ emitStaticHtml: true })` gets sitemap/robots/llms for free
   * with zero per-site edits. Set explicitly to `false` to keep emitting
   * per-route HTML without the site files. The whole block is wrapped in the
   * same try/catch + existsSync guards, so it can never break a build.
   */
  emitSiteFiles?: boolean

  /**
   * Canonical production origin, e.g. `https://ironoakcontractors.com`. Used as
   * the sitemap/llms base and the absolute `Sitemap:` line in robots.txt. When
   * absent, falls back to `seo.yaml`'s `global.siteUrl`; if neither is known and
   * no per-page canonicals exist, the sitemap + llms.txt no-op (robots still
   * emits). Never guessed.
   */
  siteUrl?: string

  /** robots.txt override hooks (disallow/allow/extra/aiBots/force). */
  robots?: DcsRobotsOptions

  /**
   * Preview / staging gate. When `true`, robots.txt becomes `Disallow: /` (no
   * `Sitemap:` line) and neither sitemap.xml nor llms.txt is emitted (privacy).
   * Detected ONLY via this explicit option — never guessed.
   */
  preview?: boolean

  /** Emit `dist/llms.txt` (default `true`; always off in preview mode). */
  llms?: boolean

  // ── SEO emission honesty rails (C-334) ────────────────────────────────────
  // Three asserts against one failure class: a capability that exists is
  // silently undone downstream and nothing notices. All three default ON
  // (severity `error`) whenever `emitStaticHtml` is on, and all three can be
  // lowered per-site from `.dcs/seo.yaml` (portal-owned) or per-build from an
  // env var that prints in the log. There is no silent off switch.

  /**
   * P1 — assert that each route's BAKED `<title>`/description equals what the
   * app leaves in the `<head>` after it mounts. Rides the prerender browser, so
   * the marginal cost is one `page.evaluate()` per route.
   *
   * Env override: `DCS_SEO_HEAD_HONESTY=error|warn|off`.
   * seo.yaml override: `headHonesty: warn` or `headHonesty: { mode, allow }`.
   */
  headHonesty?: boolean | HonestyMode | SeoHonestyBlock

  /**
   * P2 — assert that every URL the factory publishes (JSON-LD logo/image IRIs,
   * `og:image`, icon links, sitemap `<loc>`s, llms.txt links) resolves to what it
   * promises. Same-origin assets are proven against `dist/` with no network;
   * cross-origin assets are probed under a cache + time budget and only a
   * DEFINITIVE wrong answer fails the build.
   *
   * Env override: `DCS_SEO_URL_HONESTY=error|warn|off`,
   * `DCS_SEO_URL_HONESTY_NETWORK=off` to skip the cross-origin probes.
   */
  urlHonesty?: boolean | HonestyMode | SeoHonestyBlock

  /**
   * P12 — hoist `<meta charset>` to the top of `<head>` and assert it lands
   * inside the spec's 1024-byte encoding-sniffing window.
   *
   * Env override: `DCS_SEO_CHARSET_BUDGET=error|warn|off`.
   */
  charsetBudget?: boolean | HonestyMode | SeoHonestyBlock
}

/** Split a `boolean | mode | { mode, allow, … }` option into its parts. */
function readHonestyBlock(
  value: boolean | HonestyMode | SeoHonestyBlock | undefined
): { mode?: HonestyMode | boolean; allow: string[]; checkDescription?: boolean; network?: boolean } {
  if (value === undefined) return { allow: [] }
  if (typeof value === 'boolean' || typeof value === 'string') return { mode: value, allow: [] }
  return {
    mode: value.mode,
    allow: Array.isArray(value.allow) ? value.allow : [],
    checkDescription: value.checkDescription,
    network: value.network,
  }
}

/** Resolve the SPA output directory from Vite config (absolute path). */
function resolveOutDir(config: ResolvedConfig): string {
  const out = config.build?.outDir || 'dist'
  return path.isAbsolute(out) ? out : path.resolve(config.root || process.cwd(), out)
}

/**
 * Load + parse seo.yaml from the usual `.dcs` locations.
 * Returns `{ config, foundPath }` or `null` (missing/unparseable).
 */
function loadSeoConfig(
  projectRoot: string,
  seoPath: string,
  debug: boolean
): { config: SeoConfiguration; foundPath: string } | null {
  const possiblePaths = [
    path.resolve(projectRoot, seoPath),
    path.resolve(projectRoot, '..', seoPath),
    path.resolve(process.cwd(), seoPath),
  ]

  let foundPath: string | undefined
  for (const testPath of possiblePaths) {
    if (fs.existsSync(testPath)) {
      foundPath = testPath
      break
    }
  }

  if (!foundPath) {
    if (debug) {
      console.log('[dcs-seo] No seo.yaml found at:')
      possiblePaths.forEach((p) => console.log(`  - ${p}`))
      console.log('[dcs-seo] Using defaults only')
    }
    return null
  }

  try {
    const fileContent = fs.readFileSync(foundPath, 'utf8')
    const config = yaml.load(fileContent) as SeoConfiguration
    return { config, foundPath }
  } catch (error) {
    console.warn('[dcs-seo] Failed to parse seo.yaml:', error)
    return null
  }
}

/**
 * Load + parse `.dcs/content.yaml` from the usual `.dcs` locations. Returns the
 * parsed config or `undefined` (missing/unparseable). Used to thread REAL
 * reviews into the honest Review/aggregateRating builder. Never throws.
 */
function loadContentConfig(
  projectRoot: string,
  contentRelPath: string,
  debug: boolean
): ContentConfig | undefined {
  const possiblePaths = [
    path.resolve(projectRoot, contentRelPath),
    path.resolve(projectRoot, '..', contentRelPath),
    path.resolve(process.cwd(), contentRelPath),
  ]
  const foundPath = possiblePaths.find((p) => fs.existsSync(p))
  if (!foundPath) {
    if (debug) console.log(`[dcs-seo] No content.yaml found (reviews disabled)`)
    return undefined
  }
  try {
    const parsed = yaml.load(fs.readFileSync(foundPath, 'utf8'))
    if (!parsed || typeof parsed !== 'object') return undefined
    return parsed as ContentConfig
  } catch (error) {
    console.warn('[dcs-seo] Failed to parse content.yaml (reviews disabled):', error)
    return undefined
  }
}

/** Extract the optional top-level `excluded:` glob list from pages.yaml. */
function loadExcludedGlobs(projectRoot: string, pagesPath: string): string[] {
  const possiblePaths = [
    path.resolve(projectRoot, pagesPath),
    path.resolve(projectRoot, '..', pagesPath),
    path.resolve(process.cwd(), pagesPath),
  ]
  const found = possiblePaths.find((p) => fs.existsSync(p))
  if (!found) return []
  try {
    const raw = yaml.load(fs.readFileSync(found, 'utf8')) as { excluded?: unknown } | null
    const excluded = raw?.excluded
    if (!Array.isArray(excluded)) return []
    return excluded.filter((e): e is string => typeof e === 'string')
  } catch {
    return []
  }
}

/**
 * Refuse a manifest whose route paths collide after trailing-slash
 * normalization (C-361 F6).
 *
 * ONE OF TWO PLACES THE PLUGIN DELIBERATELY THROWS (the other is
 * {@link assertNoOrphanNoindexEntries}). Everything else in the
 * emit path is defensive by design — a missing or unparseable input warns and
 * no-ops, because a bad input must never break a production build. A duplicate
 * normalized path is a different animal: it is not a missing input, it is an
 * input for which every possible behaviour is wrong. The emitter's last write
 * wins the file on disk; the runtime lookup's first entry wins the head. Picking
 * a side ships a GUARANTEED baked/runtime divergence and calls it success —
 * exactly the silent-wrong-answer class this plan exists to kill — and the two
 * colliding entries have different slugs, so only the owner knows which page
 * that URL is. A red build is the cheap failure here; a green one is not.
 *
 * Thrown from `config()`, so it fails before a single byte is emitted.
 */
function assertNoDuplicateRoutePaths(routes: PageRouteEntry[]): void {
  const dupes = findDuplicateNormalizedPaths(routes)
  if (dupes.length === 0) return
  throw new Error(`[dcs-seo] ${formatDuplicateNormalizedPaths(dupes)}`)
}

/**
 * Every key a `noindex` entry can legitimately match.
 *
 * The plugin's contract for `noindex` is a PREDICATE OVER THE ROUTE MANIFEST,
 * never a directive about a bare URL. Every consumer asks the question
 * route-first and identically —
 * `noindexSet.has(route.path) || (route.slug && noindexSet.has(route.slug))` —
 * in {@link emitStaticSeoHtml}, in the sitemap/llms `isRouteIndexable`
 * predicate, and in the baked `__DCS_PAGES__` manifest the runtime re-assert
 * reads. So the set of entries that can ever do anything is exactly the union
 * of the manifest's paths and slugs, and this function is the exact inverse of
 * that same relation rather than a second, drifting implementation of it.
 */
export function collectNoindexMatchKeys(routes: PageRouteEntry[]): Set<string> {
  const keys = new Set<string>()
  for (const route of routes) {
    if (route.path) keys.add(route.path)
    if (route.slug) keys.add(route.slug)
  }
  return keys
}

/**
 * `noindex` entries that match no route in the manifest — i.e. entries that
 * are, today, silent no-ops.
 *
 * Matched with the SAME raw string equality the consumers use, deliberately:
 * a `'/account/'` entry against a `'/account'` route does not match in
 * `emitStaticSeoHtml`, so it must not "match" here either, or the check would
 * bless the very entry the emitter is about to ignore.
 */
export function findOrphanNoindexEntries(
  noindex: string[],
  routes: PageRouteEntry[]
): string[] {
  const known = collectNoindexMatchKeys(routes)
  const seen = new Set<string>()
  const orphans: string[] = []
  for (const entry of noindex) {
    if (known.has(entry) || seen.has(entry)) continue
    seen.add(entry)
    orphans.push(entry)
  }
  return orphans
}

/** Thrown when a `noindex` entry names nothing in `pages.yaml` (C-416). */
export class NoindexOrphanError extends Error {
  readonly orphans: string[]
  readonly knownKeys: string[]
  constructor(orphans: string[], knownKeys: string[]) {
    super(
      `[dcs-seo] noindex entr${orphans.length === 1 ? 'y' : 'ies'} matching NO page in the ` +
        `route manifest: ${orphans.map((o) => JSON.stringify(o)).join(', ')}. ` +
        `A noindex entry is a predicate over pages.yaml, not a standalone directive: ` +
        `with no matching page NOTHING is emitted for that route, so the entry is a ` +
        `silent no-op and the URL stays "index, follow" to crawlers — which is exactly ` +
        `how six homepage-clone URLs on KEPT stayed indexable. ` +
        `Fix it by adding the page to pages.yaml (it then gets a real ` +
        `robots: noindex, nofollow document, sitemap/llms omission, and a runtime ` +
        `re-assert), or by deleting the stale entry. ` +
        `Matchable keys are the manifest's paths and slugs: ` +
        `${knownKeys.map((k) => JSON.stringify(k)).join(', ') || '(manifest is empty)'}`
    )
    this.name = 'NoindexOrphanError'
    this.orphans = orphans
    this.knownKeys = knownKeys
  }
}

/**
 * Refuse a `noindex` list containing an entry that matches no page (C-416).
 *
 * THE SECOND PLACE THE PLUGIN DELIBERATELY THROWS, and for the same reason as
 * {@link assertNoDuplicateRoutePaths}: this is not a missing input to be
 * defended against, it is an input whose only possible behaviour is wrong.
 *
 * Why a hard error and NOT "emit a noindex document for it instead":
 *  - An entry is allowed to be a bare SLUG (`'account'`), and a slug carries no
 *    URL. Synthesizing a document would mean inventing a path — inventing a
 *    crawlable URL to paper over a config typo is strictly worse than the bug
 *    being fixed.
 *  - Even for the path form, writing `dist/<path>/index.html` for a route the
 *    SPA does not have MANUFACTURES a real, fetchable URL that would otherwise
 *    never resolve. The remedy would create crawl surface, not remove it.
 *  - Adding the page to `pages.yaml` is the operator action that makes the
 *    directive real everywhere at once (emitted document + sitemap/llms
 *    omission + runtime re-assert). Anything the plugin could synthesize on its
 *    own would cover only the first of those three, i.e. a second partial
 *    capability with nothing watching — the class this whole rail family exists
 *    to kill.
 *
 * There is deliberately NO severity knob / escape hatch (unlike the C-334
 * rails, which measure emitted artifacts and can hit network or environment
 * flake). There is no legitimate orphan: the fix is always "add the page" or
 * "delete the row", both free.
 *
 * Matched against the FULL manifest, BEFORE `exclude` / `excluded:` globs are
 * applied. A route that is both excluded and noindexed names a real page, and
 * an excluded route publishes no document to be indexable — erroring there
 * would be a false positive that pushes operators to delete honest rows.
 *
 * Thrown from `config()`, so it fails before a single byte is emitted — and,
 * critically, OUTSIDE the `writeBundle` emit try/catch, which downgrades
 * everything it wraps to a `console.warn`. A version of this check placed
 * inside that catch would be vacuously green.
 */
function assertNoOrphanNoindexEntries(noindex: string[], routes: PageRouteEntry[]): void {
  if (noindex.length === 0) return
  const orphans = findOrphanNoindexEntries(noindex, routes)
  if (orphans.length === 0) return
  throw new NoindexOrphanError(orphans, [...collectNoindexMatchKeys(routes)].sort())
}

/**
 * Derive a single site-wide lastmod from seo.yaml `lastUpdated` (preferred) or
 * pages.yaml `lastUpdated`. No fabricated per-page dates.
 */
function deriveLastmod(
  seoConfig: SeoConfiguration | undefined,
  projectRoot: string,
  pagesPath: string
): string | undefined {
  const fromSeo = seoConfig?.lastUpdated
  if (typeof fromSeo === 'string' && fromSeo.length > 0) return fromSeo

  const possiblePaths = [
    path.resolve(projectRoot, pagesPath),
    path.resolve(projectRoot, '..', pagesPath),
    path.resolve(process.cwd(), pagesPath),
  ]
  const found = possiblePaths.find((p) => fs.existsSync(p))
  if (!found) return undefined
  try {
    const raw = yaml.load(fs.readFileSync(found, 'utf8')) as { lastUpdated?: unknown } | null
    const fromPages = raw?.lastUpdated
    if (typeof fromPages === 'string' && fromPages.length > 0) return fromPages
  } catch {
    /* ignore */
  }
  return undefined
}

/**
 * Core site-file emitter, separated from Vite so it is unit-testable.
 *
 * Writes the site-wide static files into `outDir`, reusing the SAME routes +
 * seoConfig already loaded by the per-route HTML loop (no second manifest read):
 *   - `sitemap.xml` (skipped in preview, or when no absolute base is derivable)
 *   - `robots.txt`  (always; preview = `Disallow: /`; don't-clobber unless force)
 *   - `llms.txt`    (skipped in preview, or when nothing indexable)
 *
 * Returns the list of relative filenames written. Pure aside from `fs` writes —
 * deterministic + idempotent. Never throws (the caller also wraps in try/catch).
 */
export function emitSiteFiles(params: {
  outDir: string
  projectRoot: string
  pagesPath: string
  routes: PageRouteEntry[]
  seoConfig: SeoConfiguration | undefined
  siteUrl?: string
  exclude?: string[]
  noindex?: string[]
  preview?: boolean
  robots?: DcsRobotsOptions
  llms?: boolean
  debug?: boolean
}): string[] {
  const {
    outDir,
    projectRoot,
    pagesPath,
    routes,
    seoConfig,
    siteUrl,
    exclude = [],
    noindex = [],
    preview = false,
    robots = {},
    llms = true,
    debug = false,
  } = params

  const written: string[] = []

  const effectiveSiteUrl = siteUrl || seoConfig?.global?.siteUrl
  const excludedGlobs = loadExcludedGlobs(projectRoot, pagesPath)
  const lastmod = deriveLastmod(seoConfig, projectRoot, pagesPath)

  // ── sitemap.xml (not in preview) ──────────────────────────────────────────
  let wroteSitemap = false
  if (!preview) {
    const xml = buildSitemapXml({
      routes,
      siteUrl: effectiveSiteUrl,
      seoConfig,
      exclude,
      noindex,
      excludedGlobs,
      lastmod,
    })
    if (xml) {
      fs.writeFileSync(path.join(outDir, 'sitemap.xml'), xml, 'utf8')
      wroteSitemap = true
      written.push('sitemap.xml')
      if (debug) console.log('[dcs-seo] wrote sitemap.xml')
    } else if (debug) {
      console.log('[dcs-seo] no absolute <loc> derivable; sitemap.xml not written')
    }
  }

  // ── robots.txt (always, unless robots.enabled is false) ───────────────────
  if (robots.enabled ?? true) {
    const robotsPath = path.join(outDir, 'robots.txt')
    // Factory-as-single-source (validated override): a hand-authored
    // public/robots.txt Vite copied into dist is KEPT only when it meets the
    // factory floor (a real `User-agent: *` group + every AI-bot tier). A stub
    // (`Sitemap:`-only, like just-posh) or an SPA-shell HTML page fails the
    // floor and is overwritten so AI discovery is guaranteed on every site.
    // `force` always overwrites; an absent file is always written.
    const existing =
      fs.existsSync(robotsPath) && !robots.force ? fs.readFileSync(robotsPath, 'utf8') : null
    const keepHandAuthored = existing !== null && isHandAuthoredRobotsAcceptable(existing)
    if (keepHandAuthored) {
      if (debug)
        console.log('[dcs-seo] dist/robots.txt is hand-authored and meets the floor; leaving it')
    } else {
      const txt = buildRobotsTxt({
        siteUrl: effectiveSiteUrl,
        preview,
        robots,
        hasSitemap: wroteSitemap,
      })
      fs.writeFileSync(robotsPath, txt, 'utf8')
      written.push('robots.txt')
      if (debug) {
        console.log(
          existing !== null
            ? '[dcs-seo] dist/robots.txt failed the factory floor; overwrote with emitted robots'
            : '[dcs-seo] wrote robots.txt'
        )
      }
    }
  }

  // ── llms.txt (not in preview) ─────────────────────────────────────────────
  if (llms && !preview) {
    const txt = buildLlmsTxt({
      routes,
      siteUrl: effectiveSiteUrl,
      seoConfig,
      exclude,
      noindex,
      excludedGlobs,
    })
    if (txt) {
      fs.writeFileSync(path.join(outDir, 'llms.txt'), txt, 'utf8')
      written.push('llms.txt')
      if (debug) console.log('[dcs-seo] wrote llms.txt')
    } else if (debug) {
      console.log('[dcs-seo] nothing indexable; llms.txt not written')
    }
  }

  return written
}

/**
 * Pick a structured FAQ array (`[{ q, a }]`) for a page from content.yaml, if
 * one exists under a `faq` key (page-scoped, then global). Returns `undefined`
 * when there is no structured Q&A — a `<FaqPage />` component is NOT a source,
 * so its pages correctly yield nothing (honesty).
 */
function pickPageFaq(
  content: ContentConfig | undefined,
  pageSlug: string
): FaqSource[] | undefined {
  if (!content) return undefined
  const fromBlock = (block: Record<string, unknown> | undefined): FaqSource[] | undefined => {
    if (!block) return undefined
    const v = block['faq']
    return Array.isArray(v) ? (v as FaqSource[]) : undefined
  }
  return fromBlock(content.pages?.[pageSlug]) ?? fromBlock(content.global)
}

/**
 * Core emitter, separated from Vite so it is unit-testable.
 *
 * For each route: build the head tags from the shared resolver, splice them
 * into the shell HTML, and write `dist/<path>/index.html`. Returns the number
 * of files written. Pure aside from `fs` writes — deterministic + idempotent.
 */
export function emitStaticSeoHtml(params: {
  outDir: string
  shellHtml: string
  routes: PageRouteEntry[]
  seoConfig: SeoConfiguration | undefined
  /**
   * Parsed `.dcs/content.yaml` — threads REAL review items (honesty-gated) into
   * the per-route LocalBusiness node. Optional; omit ⇒ no Review/aggregateRating.
   */
  contentConfig?: ContentConfig
  exclude?: string[]
  noindex?: string[]
  /** `pages.yaml` top-level `excluded:` globs — routes matching are NOT emitted. */
  excludedGlobs?: string[]
  debug?: boolean
}): number {
  const {
    outDir,
    shellHtml,
    routes,
    seoConfig,
    contentConfig,
    exclude = [],
    noindex = [],
    excludedGlobs = [],
    debug = false,
  } = params

  const noindexSet = new Set(noindex)

  // Route → human title map (for breadcrumb hop labels), from the manifest.
  const siteUrl = seoConfig?.global?.siteUrl ?? ''
  const routeTitles: Record<string, string> = { '/': 'Home' }
  for (const r of routes) {
    if (r.title && r.path) routeTitles[r.path.replace(/\/+$/, '') || '/'] = r.title
  }

  let written = 0
  for (const route of routes) {
    // Exclusion goes through the SHARED predicate (`routeExclusionReason`), the
    // same one that filters the `__DCS_PAGES__` runtime manifest. Two agreeing
    // implementations is how a private `/dev-*` route came to be never-baked but
    // still runtime-asserted (C-361 F5); one predicate is why it cannot recur.
    // An excluded route must NOT get a per-route index.html, or it becomes a
    // crawlable/indexable page despite being intentionally hidden — a privacy
    // and crawl-budget leak. Same glob semantics as the sitemap path.
    const exclusion = routeExclusionReason(route, { exclude, excludedGlobs })
    if (exclusion) {
      if (debug) {
        console.log(
          `[dcs-seo] skip (${exclusion === 'excluded-glob' ? 'excluded glob' : 'excluded'}): ${route.path}`
        )
      }
      continue
    }

    const forceNoindex =
      noindexSet.has(route.path) || (route.slug && noindexSet.has(route.slug))

    // Breadcrumb trail from route depth (home ⇒ none); titles from the manifest.
    const breadcrumbTrail = siteUrl
      ? breadcrumbTrailFromRoute(route.path, siteUrl, routeTitles)
      : undefined

    // REAL reviews for this page from content.yaml (honesty-gated downstream).
    const reviews: ReviewSource[] | undefined = contentConfig
      ? findReviewItemsForPage(contentConfig, route.slug)
      : undefined

    // Trade/occupational license from content.yaml `business.license` (dark when
    // the key is empty/absent — no hasCredential is emitted). Honesty-gated in
    // buildHasCredential downstream.
    const license: string | undefined = contentConfig
      ? findBusinessLicense(contentConfig, route.slug)
      : undefined

    // BlogPosting for a blog post route — only when seo.yaml has NOT already
    // hand-authored one (buildHeadTags skips ours if it has). A blog post is a
    // route nested under `/blog/` (depth ≥ 2) with a manifest title.
    const isBlogPost = /^\/blog\/.+/.test(route.path) && !!route.title
    const blogMeta: BlogMeta | undefined = isBlogPost
      ? { headline: route.title, url: breadcrumbTrail?.[breadcrumbTrail.length - 1]?.item }
      : undefined

    // FAQ from a structured `.dcs/faq.yaml`-style block on the content page, if
    // present (honesty-gated). Component-only FAQ pages carry no such data.
    const faq: FaqSource[] | undefined = pickPageFaq(contentConfig, route.slug)

    const tags = buildHeadTags(route.slug, route.path, seoConfig, {
      includeKeywords: true,
      fallbackTitle: route.title,
      robots: forceNoindex ? 'noindex, nofollow' : undefined,
      emitGraph: true,
      breadcrumbTrail,
      reviews,
      license,
      blogMeta,
      faq,
    })

    const html = spliceHeadHtml(shellHtml, tags)
    const outFile = routeToOutputFile(outDir, route.path)

    fs.mkdirSync(path.dirname(outFile), { recursive: true })
    fs.writeFileSync(outFile, html, 'utf8')
    written++

    if (debug) {
      console.log(
        `[dcs-seo] wrote ${path.relative(outDir, outFile)} (title="${tags.title}"${
          forceNoindex ? ', noindex' : ''
        })`
      )
    }
  }

  return written
}

/**
 * What the render pass produced, INCLUDING whether it ran at all. The
 * `rendererAvailable` / `eligibleRoutes` fields are what make the P1 rail
 * provably executed rather than silently skipped (C-338).
 */
interface BodyPrerenderOutcome {
  rendererAvailable: boolean
  headObservations: HeadObservation[]
  eligibleRoutes: string[]
  filteredRoutes: Array<{ route: string; reason: string }>
}

/**
 * Run the body-prerender pass: bring up the headless renderer, fill each
 * indexable route's `<div id="app">` with its real rendered DOM, then tear the
 * renderer down. Missing infra (no playwright / no browser) makes the BODY pass
 * a no-op but is reported back so the P1 rail can fail loud instead of passing
 * green having checked nothing; a render crash propagates a `BodyPrerenderError`.
 */
async function runBodyPrerender(
  ctx: { outDir: string; routes: PageRouteEntry[]; excludedGlobs: string[] },
  opts: {
    exclude: string[]
    noindex: string[]
    debug: boolean
    /** `false` ⇒ render for OBSERVATION only (P1) and leave `dist/` untouched. */
    spliceBody: boolean
  }
): Promise<BodyPrerenderOutcome> {
  // Kill-test hook (C-338): forces the renderer-absent path so the P1 execution
  // assertion can be proven to FAIL a build. NOT an escape hatch — with the
  // assertion in place, setting this in `error` mode turns the build RED.
  const forcedAbsent = process.env.DCS_SEO_FORCE_NO_RENDERER === '1'
  if (forcedAbsent) {
    console.warn(
      '[dcs-seo] DCS_SEO_FORCE_NO_RENDERER=1 — pretending the headless renderer is absent ' +
        '(kill-test hook; in error mode this MUST fail the build).'
    )
  }
  const handle = forcedAbsent
    ? null
    : await createPlaywrightRenderer({ outDir: ctx.outDir, debug: opts.debug })
  if (!handle) {
    // playwright/browser unavailable. Say so explicitly — without a renderer the
    // P1 head-honesty rail cannot run either, and a rail that silently does not
    // run is the exact class this work exists to close. The CALLER turns this
    // into a hard failure in `error` mode (see `assertHeadHonestyExecuted`).
    console.warn(
      '[dcs-seo] no headless renderer available: body prerender AND the P1 ' +
        'baked-vs-rendered <head> assert were both SKIPPED for this build.'
    )
    return {
      rendererAvailable: false,
      headObservations: [],
      eligibleRoutes: [],
      filteredRoutes: [],
    }
  }

  console.log(
    opts.spliceBody
      ? '[dcs-seo] body-prerender: rendering routes via headless Chromium…'
      : '[dcs-seo] head-honesty: rendering routes via headless Chromium (observe-only)…'
  )
  try {
    const result = await prerenderBodies({
      outDir: ctx.outDir,
      routes: ctx.routes,
      renderer: handle.renderer,
      exclude: opts.exclude,
      noindex: opts.noindex,
      excludedGlobs: ctx.excludedGlobs,
      spliceBody: opts.spliceBody,
      debug: opts.debug,
    })
    if (opts.spliceBody) {
      console.log(
        `[dcs-seo] body-prerender: ${result.prerendered} route(s) now have ` +
          `crawler-visible body content (${result.skipped} skipped)`
      )
    }
    return {
      rendererAvailable: true,
      headObservations: result.headObservations,
      eligibleRoutes: result.eligibleRoutes,
      filteredRoutes: result.filteredRoutes,
    }
  } finally {
    await handle.close()
  }
}

/**
 * P2 — collect every URL the just-completed emit published and assert it
 * resolves to what it promises.
 *
 * Deliberately two-phase, because the two halves have different reliability:
 * the same-origin half is proven against `dist/` with no network and is
 * therefore a hard gate; the cross-origin half asks the network and only fails
 * on a definitive wrong answer. See `emittedUrls.ts` for the verdict table.
 */
async function runEmittedUrlHonesty(params: {
  outDir: string
  projectRoot: string
  routes: PageRouteEntry[]
  siteUrl: string | undefined
  base: string
  mode: HonestyMode
  allow: string[]
  network: boolean
  debug: boolean
  /** Receives the audit BEFORE any throw, so the report survives a red build. */
  sink?: (audit: EmittedUrlAudit, violations: EmittedUrlViolation[]) => void
}): Promise<EmittedUrlAudit | null> {
  const { outDir, projectRoot, routes, siteUrl, base, mode, allow, network, debug, sink } = params
  if (mode === 'off') {
    console.warn(
      '[dcs-seo] emitted-URL honesty is OFF for this build — nothing is checking that the ' +
        'logo/og:image/sitemap URLs this site publishes actually resolve.'
    )
    return null
  }
  if (allow.length > 0) {
    console.warn(
      `[dcs-seo] emitted-URL honesty: ${allow.length} URL(s) EXEMPTED: ${allow.join(', ')}`
    )
  }

  const files: Array<{ path: string; from: string; kind: 'html' | 'sitemap' | 'llms' }> = []
  for (const route of routes) {
    files.push({ path: routeToOutputFile(outDir, route.path), from: route.path, kind: 'html' })
  }
  files.push({ path: path.join(outDir, 'sitemap.xml'), from: 'sitemap.xml', kind: 'sitemap' })
  files.push({ path: path.join(outDir, 'llms.txt'), from: 'llms.txt', kind: 'llms' })

  const collected = dedupeUrls(collectEmittedUrlsFromFiles(files))
  const classified: ClassifiedUrl[] = collected.map((u) => classifyUrl(u, siteUrl))

  const sameOrigin = auditSameOriginAssets({ urls: classified, outDir, base, allow })
  const violations: EmittedUrlViolation[] = [...sameOrigin.violations]

  // Every count below comes from the CHECKER, never from `classified.length` —
  // that conflation is what let C-334's log claim page URLs were verified when
  // both checkers only ever look at assets (C-338).
  const audit: EmittedUrlAudit = {
    collected: classified.length,
    sameOriginAssetsChecked: sameOrigin.checked,
    remoteAssetsChecked: 0,
    remoteProbed: 0,
    remoteCached: 0,
    unknown: 0,
    pagesUnverified: classified.filter((u) => u.expectation === 'page').length,
    unfetchable: classified.filter((u) => u.locality === 'unfetchable').length,
    exempted: sameOrigin.exempted,
    failed: 0,
  }

  if (network) {
    const remote = await checkRemoteAssets(classified, { projectRoot, allow, debug })
    violations.push(...remote.violations)
    for (const w of remote.warnings) {
      console.warn(`[dcs-seo] emitted-URL honesty (UNKNOWN, not a failure): ${w.url} — ${w.reason}`)
    }
    audit.remoteAssetsChecked = remote.checked ?? 0
    audit.remoteProbed = remote.probed
    audit.remoteCached = remote.cached
    audit.unknown = remote.warnings.length
    audit.exempted += remote.exempted ?? 0
    if (debug) {
      console.log(
        `[dcs-seo] emitted-URL honesty: remote probes — ${remote.probed} network, ${remote.cached} cached`
      )
    }
  } else {
    // Probing off ⇒ every remote asset is UNKNOWN. It must not be silently
    // absorbed into a "verified" total.
    audit.unknown = classified.filter(
      (u) => u.locality === 'remote' && u.expectation === 'asset'
    ).length
    console.log(
      '[dcs-seo] emitted-URL honesty: cross-origin probes disabled — ' +
        `${audit.unknown} remote asset URL(s) are UNCHECKED this build; same-origin still enforced`
    )
  }

  audit.failed = violations.length
  sink?.(audit, violations)
  const checkedTotal = auditCheckedTotal(audit)

  // Execution assertion: collecting nothing means the rail did not run. In
  // `error` mode that is a hard failure, not a green build.
  if (audit.collected === 0) {
    const err = new EmittedUrlNotRunError(
      audit,
      `0 URLs were collected from ${files.length} emitted artifact(s)`
    )
    if (mode === 'error') throw err
    console.warn(err.message)
    console.warn('[dcs-seo] emitted-URL honesty is in WARN mode; this build is NOT gated on the above.')
    return audit
  }
  // Collected-but-checked-nothing is legal (a site can publish only page URLs)
  // but must never read as a pass. Say it in every mode.
  if (checkedTotal === 0) {
    console.warn(
      `[dcs-seo] emitted-URL honesty VERIFIED NOTHING this build: ${audit.collected} URL(s) were ` +
        `collected but none was a checkable asset (${audit.pagesUnverified} page(s), ` +
        `${audit.unknown} unknown, ${audit.exempted} exempted, ${audit.unfetchable} unfetchable).`
    )
  }

  if (violations.length === 0) {
    console.log(formatEmittedUrlAudit(audit, routes.length))
    return audit
  }

  if (mode === 'warn') {
    console.warn(formatEmittedUrlReport(violations))
    console.warn(formatEmittedUrlAudit(audit, routes.length))
    console.warn('[dcs-seo] emitted-URL honesty is in WARN mode; this build is NOT gated on the above.')
    return audit
  }
  throw new EmittedUrlError(violations)
}

/**
 * P12 — assert the encoding declaration lands inside the 1024-byte sniffing
 * window in every emitted document.
 *
 * The hoist in `emitStaticSeoHtml`'s shell should make this unfailable; the
 * assert exists so that if the hoist is ever bypassed (a new head injector, a
 * post-processing plugin that runs later, a hand-rolled shell), the build says so
 * instead of shipping a document whose encoding is a coin flip.
 */
function runCharsetBudget(params: {
  outDir: string
  routes: PageRouteEntry[]
  mode: HonestyMode
  debug: boolean
  /** Receives the counts BEFORE any throw, so the report survives a red build. */
  sink?: (summary: { checked: number; failed: number; worstOffset: number }) => void
}): void {
  const { outDir, routes, mode, debug, sink } = params
  if (mode === 'off') return

  const checked: string[] = []
  let failed = 0
  let worstOffset = 0
  const emit = () => sink?.({ checked: checked.length, failed, worstOffset })
  for (const route of routes) {
    const file = routeToOutputFile(outDir, route.path)
    let html: string
    try {
      html = fs.readFileSync(file, 'utf8')
    } catch {
      continue
    }
    const rel = path.relative(outDir, file)
    const result = checkCharsetBudget(html)
    worstOffset = Math.max(worstOffset, result.offset)
    if (!result.ok) {
      failed += 1
      if (mode === 'warn') {
        console.warn(new CharsetBudgetError(rel, result).message)
        continue
      }
      emit()
      throw new CharsetBudgetError(rel, result)
    }
    const warning = formatCharsetHeadroomWarning(rel, result)
    if (warning) console.warn(warning)
    checked.push(rel)
  }
  emit()
  if (debug || checked.length > 0) {
    console.log(
      `[dcs-seo] charset budget: ${checked.length} emitted document(s) declare their encoding ` +
        `inside the first 1024 bytes`
    )
  }
}

/**
 * Vite plugin that injects .dcs/seo.yaml at build time and, optionally, emits
 * per-route static `<head>` HTML for SPA SEO.
 *
 * @param options - Plugin configuration
 * @returns Vite plugin
 */
export function dcsSeoPlugin(options: DcsSeoPluginOptions = {}): Plugin {
  const {
    seoPath = '.dcs/seo.yaml',
    debug = false,
    emitStaticHtml = false,
    prerenderBody,
    pagesPath = '.dcs/pages.yaml',
    contentPath = '.dcs/content.yaml',
    exclude = [],
    noindex = [],
    // Site files default ON whenever per-route HTML emission is on, so the
    // sites already using emitStaticHtml gain sitemap/robots/llms with no edit.
    emitSiteFiles: emitSiteFilesOpt = emitStaticHtml,
    siteUrl,
    headHonesty: headHonestyOpt,
    urlHonesty: urlHonestyOpt,
    charsetBudget: charsetBudgetOpt,
    robots = {},
    // Self-detect preview builds from DCS_PREVIEW (mirrors dcsEditorPlugin) so a
    // release/** SWA preview build never body-prerenders; an explicit option wins.
    preview = process.env.DCS_PREVIEW === 'true',
    llms = true,
  } = options

  let resolvedConfig: ResolvedConfig
  // Captured during configResolved — gates the serve-only inline HTML inject.
  let isDev = false

  return {
    name: 'dcs-seo',

    configResolved(config) {
      resolvedConfig = config
      isDev = config.command === 'serve'
    },

    config(config, env) {
      // ── Dev / preview (serve) ──────────────────────────────────────────────
      // Don't bake seo.yaml into the bundle. Redirect the `__DCS_SEO__` define
      // to a browser global that `transformIndexHtml` (below) refreshes from
      // disk on every HTML request — live preview with NO server.restart().
      // The consumer is untouched: it still reads the bare `__DCS_SEO__`
      // identifier, which this define rewrites to `globalThis.__DCS_SEO__`.
      // (VitePress treats `useSEO` as a no-op and bakes SEO via
      // transformPageData, so this only affects the runtime `useSEO` SPA path.)
      if (env.command === 'serve') {
        return {
          define: {
            __DCS_SEO__: 'globalThis.__DCS_SEO__',
            __DCS_PAGES__: 'globalThis.__DCS_PAGES__',
          },
        }
      }

      // ── Production build ───────────────────────────────────────────────────
      // Unchanged: read seo.yaml now and bake it into the bundle as a literal.
      const projectRoot = config.root || process.cwd()

      // The ROUTE MANIFEST for the runtime re-assert (`installSeoHead`).
      //
      // It is baked here — not read from `pages.yaml` at runtime — so the router
      // hook resolves against the SAME map the build-time emitter walked. The
      // `noindex` list rides along because it is a plugin OPTION, not a file:
      // without it the runtime cannot know a route was baked `noindex, nofollow`
      // and would republish `index, follow` over it (measured live on iron-oak
      // /account and /projects, C-357).
      //
      // Emitted unconditionally, independent of the seo.yaml branch below —
      // `installSeoHead` needs it in BOTH cases, and a bare `__DCS_PAGES__`
      // identifier with no define is a ReferenceError in a consumer bundle.
      // IT MODELS WHAT THE EMITTER WRITES, NOT WHAT pages.yaml DECLARES
      // (C-361 F5). Routes the emitter SKIPS — `exclude` entries and `excluded:`
      // globs — are omitted here too, through the SAME `routeExclusionReason`
      // predicate the emit loop calls. Without that filter a private `/dev-*`
      // route was never baked (correct) yet still runtime-asserted with a
      // canonical and `index, follow` on the first in-app navigation (wrong):
      // the manifest promised the runtime a page the build had deliberately
      // refused to publish. Omitting is chosen over carrying exclude semantics
      // into the browser because it needs no glob engine at runtime and cannot
      // disagree with the emitter by construction.
      const pagesDefine = ((): string => {
        const routes = loadPagesManifest(projectRoot, pagesPath, debug)
        if (!routes) {
          // No manifest ⇒ no per-route emission and no runtime manifest, so a
          // noindex list here is inert everywhere. Not a hard failure (the
          // missing manifest is its own, already-warned condition), but say so
          // rather than let the operator believe the directive landed.
          if (noindex.length > 0) {
            console.warn(
              `[dcs-seo] noindex has ${noindex.length} entr${noindex.length === 1 ? 'y' : 'ies'} ` +
                `but ${pagesPath} is missing or empty — the list matches nothing and is inert.`
            )
          }
          return 'undefined'
        }
        // C-416: a noindex entry naming no page emits NO document, so the
        // directive silently does nothing and the URL stays crawlable. Checked
        // against the FULL manifest, before exclusion, and thrown from here so
        // it cannot be swallowed by the writeBundle emit try/catch.
        assertNoOrphanNoindexEntries(noindex, routes)
        const excludedGlobs = loadExcludedGlobs(projectRoot, pagesPath)
        const emitted = routes.filter((r) =>
          isRouteEmitted(r, { exclude, excludedGlobs })
        )
        assertNoDuplicateRoutePaths(emitted)
        return JSON.stringify({
          routes: emitted.map((r) => ({
            slug: r.slug,
            path: r.path,
            ...(r.title ? { title: r.title } : {}),
          })),
          noindex,
        })
      })()

      const loaded = loadSeoConfig(projectRoot, seoPath, debug)
      if (!loaded) {
        return {
          define: {
            __DCS_SEO__: 'undefined',
            __DCS_PAGES__: pagesDefine,
          },
        }
      }

      if (debug) {
        console.log(`[dcs-seo] Loaded ${loaded.foundPath}`)
        console.log(`[dcs-seo] Version: ${loaded.config.version}`)
        console.log(`[dcs-seo] Site Name: ${loaded.config.global?.siteName || '(not set)'}`)
        console.log(
          `[dcs-seo] Pages: ${Object.keys(loaded.config.pages ?? {}).join(', ') || '(none)'}`
        )
      }

      return {
        define: {
          __DCS_SEO__: JSON.stringify(loaded.config),
          __DCS_PAGES__: pagesDefine,
        },
      }
    },

    /**
     * Build-time static `<head>` emitter (opt-in via `emitStaticHtml`).
     *
     * Runs after the bundle is written so the built `index.html` shell exists
     * on disk. For an SPA, Vite emits a single `index.html`; we fan it out to
     * per-route files with route-specific `<head>` content.
     *
     * Fully defensive: any missing/unparseable input logs a warning and
     * no-ops. Never throws (so it can never break a production build).
     */
    async writeBundle() {
      if (!emitStaticHtml) {
        // The whole emit path is gated on emitStaticHtml. If the operator
        // explicitly asked for site files (`emitSiteFiles: true`) while
        // emitStaticHtml is off, that request is silently inert — warn so it's
        // not mistaken for "robots/sitemap were emitted".
        if (options.emitSiteFiles === true) {
          console.warn(
            '[dcs-seo] emitSiteFiles:true is ignored because emitStaticHtml is false; ' +
              'set emitStaticHtml:true to emit sitemap.xml/robots.txt/llms.txt'
          )
        }
        return
      }

      // Captured inside the never-break emit below, consumed by the separately
      // guarded body-prerender step after it (so a fabrication/crash there can
      // fail the build loud instead of being swallowed by the emit try/catch).
      let bodyPrerenderCtx:
        | { outDir: string; routes: PageRouteEntry[]; excludedGlobs: string[] }
        | null = null

      /** P12 shell measurement (before/after the charset hoist), for the report. */
      let charsetShell: {
        hoisted: boolean
        beforeOffset: number
        beforeHeadroom: number
        beforeOk: boolean
        afterOffset: number
        afterHeadroom: number
        afterOk: boolean
      } | null = null

      // Same pattern for the C-334 honesty rails: gathered inside the
      // never-break emit, EXECUTED outside it. An emitter that fails is a
      // degraded build; a rail that fails is a build that must go red — so the
      // rails must not be swallowed by the emitter's defensive catch. (That
      // swallow is precisely how a check becomes vacuously green.)
      let honestyCtx:
        | {
            outDir: string
            projectRoot: string
            routes: PageRouteEntry[]
            siteUrl: string | undefined
            base: string
            headMode: HonestyMode
            headAllow: string[]
            headCheckDescription: boolean
            urlMode: HonestyMode
            urlAllow: string[]
            urlNetwork: boolean
            charsetMode: HonestyMode
            /** `true` when the render pass runs only to feed the P1 rail. */
            renderForHonestyOnly: boolean
          }
        | null = null

      try {
        const projectRoot = resolvedConfig?.root || process.cwd()
        const outDir = resolveOutDir(resolvedConfig)

        if (!fs.existsSync(outDir)) {
          console.warn(`[dcs-seo] emitStaticHtml: outDir not found (${outDir}); skipping`)
          return
        }

        const shellPath = path.join(outDir, 'index.html')
        if (!fs.existsSync(shellPath)) {
          console.warn(
            `[dcs-seo] emitStaticHtml: no index.html in ${outDir}; nothing to emit`
          )
          return
        }

        const routes = loadPagesManifest(projectRoot, pagesPath, debug)
        if (!routes) {
          console.warn(
            `[dcs-seo] emitStaticHtml: ${pagesPath} missing or empty; skipping per-route emission`
          )
          return
        }

        const loaded = loadSeoConfig(projectRoot, seoPath, debug)
        if (!loaded) {
          // seo.yaml is optional for emission — routes still get baked with
          // global/runtime defaults (which here means just the shell title and
          // a derived canonical-less head). Warn so the operator knows.
          console.warn(
            `[dcs-seo] emitStaticHtml: ${seoPath} missing or unparseable; emitting with defaults only`
          )
        }

        // ── P12: hoist <meta charset> before anything is fanned out ─────────
        // The motion-token <style> is injected `head-prepend`, which lands it
        // ABOVE the site's own <meta charset> and pushes the encoding
        // declaration to byte ~910 of the spec's 1024-byte sniffing window
        // (measured identical on two fleet sites, 2026-07-26). Fixing the SHELL
        // fixes every per-route file the loop below writes from it, in one place,
        // with no per-site edit. `hoistCharsetMeta` is idempotent, so a shell
        // that is already correct is untouched.
        const rawShellHtml = fs.readFileSync(shellPath, 'utf8')
        const shellHtml = hoistCharsetMeta(rawShellHtml)
        // Recorded for the honesty report: the BEFORE/AFTER byte offsets are the
        // P12 measurement the fleet matrix asks for, per site.
        {
          const b = checkCharsetBudget(rawShellHtml)
          const a = checkCharsetBudget(shellHtml)
          charsetShell = {
            hoisted: shellHtml !== rawShellHtml,
            beforeOffset: b.offset,
            beforeHeadroom: b.headroom,
            beforeOk: b.ok,
            afterOffset: a.offset,
            afterHeadroom: a.headroom,
            afterOk: a.ok,
          }
        }
        if (shellHtml !== rawShellHtml) {
          // Write it back too: `dist/index.html` is what the host serves as the
          // SPA fallback for every unmatched path, and a route manifest without
          // `/` would otherwise leave the shell un-hoisted.
          fs.writeFileSync(shellPath, shellHtml, 'utf8')
          const before = checkCharsetBudget(rawShellHtml)
          const after = checkCharsetBudget(shellHtml)
          console.log(
            `[dcs-seo] charset budget: hoisted <meta charset> from byte ${before.offset} ` +
              `to byte ${after.offset} (${after.headroom} bytes of the 1024-byte ` +
              `encoding-sniffing window now free)`
          )
        }

        // pages.yaml top-level `excluded:` globs gate BOTH the per-route HTML
        // emission below and the sitemap/llms set inside emitSiteFiles, so an
        // excluded route is never written as a crawlable index.html.
        const excludedGlobs = loadExcludedGlobs(projectRoot, pagesPath)
        // Optional: load content.yaml so REAL reviews can feed the honest
        // Review/aggregateRating on the per-route LocalBusiness node. Absent or
        // unparseable ⇒ undefined ⇒ no reviews emitted (no synthesis).
        const contentConfig = loadContentConfig(projectRoot, contentPath, debug)

        // C-1260 finding 3 — a LocalBusiness `address` that is PRESENT BUT
        // PARTIAL is spread verbatim into the @graph by `buildGlobalGraph`, so
        // it reaches crawlers looking finished. WARN only: the emitted shape is
        // deliberately unchanged (removing a half-address would silently drop
        // authored data), and an ABSENT address is never reported because a
        // service-area business is supposed to omit it.
        const addressGaps = findPostalAddressGaps(loaded?.config?.global ?? {})
        if (addressGaps.length > 0) {
          console.warn(formatPostalAddressGapReport(addressGaps))
        }

        const written = emitStaticSeoHtml({
          outDir,
          shellHtml,
          routes,
          seoConfig: loaded?.config,
          contentConfig,
          exclude,
          noindex,
          excludedGlobs,
          debug,
        })

        if (debug) {
          console.log(`[dcs-seo] emitStaticHtml: wrote ${written} per-route HTML file(s)`)
        }

        // ── site-wide static files (sitemap.xml, robots.txt, llms.txt) ──────
        // Emitted AFTER the per-route HTML loop so canonicals/routes are
        // resolved and the shell exists. Reuses the already-loaded routes +
        // seoConfig (no second manifest read). Same outDir/existsSync guard +
        // the outer try/catch — so it can never break a production build.
        if (emitSiteFilesOpt) {
          const siteFiles = emitSiteFiles({
            outDir,
            projectRoot,
            pagesPath,
            routes,
            seoConfig: loaded?.config,
            siteUrl,
            exclude,
            noindex,
            preview,
            robots,
            llms,
            debug,
          })
          if (debug) {
            console.log(
              `[dcs-seo] emitSiteFiles: wrote ${siteFiles.length} site file(s): ${
                siteFiles.join(', ') || '(none)'
              }`
            )
          }
        }

        // Body prerender runs only when enabled for this build (default ON when
        // emitting; off via the `prerenderBody` option, the seo.yaml escape
        // hatch, or preview mode). Stash what it needs so it executes under its
        // own fail-loud guard below, NOT this swallow-catch.
        // Extra guard: the prerender server serves the built assets from root,
        // so a non-root `base` (subpath preview deploys like /kept/) would 404
        // every asset → the app never mounts → a spurious build failure. Skip
        // (loudly) rather than fail — head + JSON-LD are already emitted.
        const buildBase = resolvedConfig?.base ?? '/'
        const baseIsRoot = buildBase === '/' || buildBase === './' || buildBase === ''
        const bodyPrerenderOn = isBodyPrerenderEnabled({
          emitStaticHtml,
          prerenderBodyOption: prerenderBody,
          preview,
          seoPrerenderBody: loaded?.config?.prerenderBody,
        })
        if (bodyPrerenderOn) {
          if (baseIsRoot) {
            bodyPrerenderCtx = { outDir, routes, excludedGlobs }
          } else {
            console.warn(
              `[dcs-seo] body prerender skipped: non-root base "${buildBase}" ` +
                '(assets would not resolve through the prerender server; head + JSON-LD still emitted)',
            )
          }
        }

        // ── C-334 honesty rails: resolve severities, stash the context ───────
        // Resolution order is env > seo.yaml > plugin option > default `error`,
        // so the most operator-visible signal wins and a lowered gate always
        // shows up either in git (seo.yaml) or in the build log (env).
        // `*OptBlock` = the vite.config option; `seo*Block` = the .dcs/seo.yaml
        // escape hatch. Kept in matching pairs so the precedence below reads at a
        // glance and a future edit cannot silently swap the two sources.
        const headOptBlock = readHonestyBlock(headHonestyOpt)
        const urlOptBlock = readHonestyBlock(urlHonestyOpt)
        const charsetOptBlock = readHonestyBlock(charsetBudgetOpt)
        const seoHeadBlock = readHonestyBlock(loaded?.config?.headHonesty ?? undefined)
        const seoUrlBlock = readHonestyBlock(loaded?.config?.urlHonesty ?? undefined)
        const seoCharsetBlock = readHonestyBlock(loaded?.config?.charsetBudget ?? undefined)

        const headMode = resolveHonestyMode({
          env: process.env.DCS_SEO_HEAD_HONESTY,
          seoYaml: seoHeadBlock.mode,
          option: headOptBlock.mode,
          fallback: 'error',
        })
        const urlMode = resolveHonestyMode({
          env: process.env.DCS_SEO_URL_HONESTY,
          seoYaml: seoUrlBlock.mode,
          option: urlOptBlock.mode,
          fallback: 'error',
        })
        const charsetMode = resolveHonestyMode({
          env: process.env.DCS_SEO_CHARSET_BUDGET,
          seoYaml: seoCharsetBlock.mode,
          option: charsetOptBlock.mode,
          fallback: 'error',
        })

        // The P1 rail needs a render. If body prerender is already running, it
        // rides along for free; if the site opted out of body prerender we still
        // render — in OBSERVE-ONLY mode, so `dist/` is untouched — because the
        // alternative is a gate that quietly does not exist on that site.
        const renderForHonestyOnly = !bodyPrerenderOn && headMode !== 'off' && baseIsRoot
        if (renderForHonestyOnly) {
          bodyPrerenderCtx = { outDir, routes, excludedGlobs }
        }
        if (headMode !== 'off' && !baseIsRoot) {
          console.warn(
            `[dcs-seo] head honesty SKIPPED: non-root base "${buildBase}" (the prerender ` +
              'server cannot serve this build, so the rendered <head> cannot be observed)'
          )
        }

        honestyCtx = {
          outDir,
          projectRoot,
          routes,
          siteUrl: siteUrl || loaded?.config?.global?.siteUrl,
          base: buildBase,
          headMode: baseIsRoot ? headMode : 'off',
          headAllow: [...headOptBlock.allow, ...seoHeadBlock.allow],
          headCheckDescription:
            seoHeadBlock.checkDescription ?? headOptBlock.checkDescription ?? true,
          urlMode,
          urlAllow: [...urlOptBlock.allow, ...seoUrlBlock.allow],
          urlNetwork:
            process.env.DCS_SEO_URL_HONESTY_NETWORK?.toLowerCase() === 'off'
              ? false
              : (seoUrlBlock.network ?? urlOptBlock.network ?? true),
          charsetMode,
          renderForHonestyOnly,
        }
      } catch (error) {
        // Never break the build on SEO emission failure.
        console.warn('[dcs-seo] emitStaticHtml failed; build output left unchanged:', error)
      }

      // ── Body prerender + P1 (SEPARATE guard — fail loud) ─────────────────────
      // Deliberately OUTSIDE the swallow-catch above: missing infra (no
      // playwright / no browser) degrades gracefully to a no-op, but a route that
      // crashes or would ship broken content throws BodyPrerenderError and FAILS
      // the build. Head + JSON-LD are already on disk regardless.
      let headObservations: HeadObservation[] = []
      // Execution facts for the P1 assertion below. `rendererAvailable` starts
      // FALSE: if the render pass never ran, the rail must say so rather than
      // inherit an optimistic default.
      let rendererAvailable = false
      let eligibleRoutes: string[] = []
      let filteredRoutes: Array<{ route: string; reason: string }> = []
      if (bodyPrerenderCtx) {
        const outcome = await runBodyPrerender(bodyPrerenderCtx, {
          exclude,
          noindex,
          debug,
          spliceBody: !(honestyCtx?.renderForHonestyOnly ?? false),
        })
        headObservations = outcome.headObservations
        rendererAvailable = outcome.rendererAvailable
        eligibleRoutes = outcome.eligibleRoutes
        filteredRoutes = outcome.filteredRoutes
      }

      if (honestyCtx) {
        const ctx = honestyCtx
        // Machine-readable execution record (C-338). Written only when
        // DCS_SEO_HONESTY_REPORT names a path, so normal builds are untouched;
        // the fleet candidate-matrix harness sets it and reads counts from JSON
        // instead of scraping the build log. Written in a `finally` so a RED
        // build still leaves its measurement behind.
        const reportPath = process.env.DCS_SEO_HONESTY_REPORT
        const report: Record<string, unknown> = {
          schema: 1,
          generatedAt: new Date().toISOString(),
          projectRoot: ctx.projectRoot,
          outDir: ctx.outDir,
          siteUrl: ctx.siteUrl ?? null,
          base: ctx.base,
          routes: ctx.routes.length,
          p1: {
            mode: ctx.headMode,
            rendererAvailable,
            eligible: eligibleRoutes.length,
            observed: headObservations.length,
            unobserved: eligibleRoutes.filter(
              (r) => !headObservations.some((o) => o.route === r)
            ),
            filtered: filteredRoutes,
            exempted: ctx.headAllow.length,
            divergent: null as number | null,
            executed: false,
          },
          p12: { mode: ctx.charsetMode, shell: charsetShell, checked: 0, failed: 0, worstOffset: 0 },
          p2: { mode: ctx.urlMode, network: ctx.urlNetwork, audit: null as unknown },
        }
        const p1 = report.p1 as Record<string, unknown>
        try {
          // P1 — baked <head> == rendered <head>, per route. The EXECUTION
          // assertion runs first: in `error` mode a missing renderer, zero
          // eligible routes, or an eligible route with no observation is a hard
          // failure. C-334 warned and skipped here, which is how an
          // error-severity rail passed a build having checked nothing (C-338).
          const shouldCompare = assertHeadHonestyExecuted(
            {
              rendererAvailable,
              eligibleRoutes,
              observedRoutes: headObservations.map((o) => o.route),
              filteredRoutes,
              totalRoutes: ctx.routes.length,
            },
            { mode: ctx.headMode }
          )
          p1.executed = shouldCompare
          if (shouldCompare || ctx.headMode === 'off') {
            // In `error` mode this throws — record the divergences FIRST so a red
            // build still leaves the operator the list of what diverged.
            const found = findHeadHonestyViolations(headObservations, {
              allow: ctx.headAllow,
              checkDescription: ctx.headCheckDescription,
            })
            p1.divergent = found.length
            p1.violations = found
            assertHeadHonesty(headObservations, {
              mode: ctx.headMode,
              allow: ctx.headAllow,
              checkDescription: ctx.headCheckDescription,
              debug,
            })
          }

          // P12 — the encoding declaration is inside the sniffing window.
          runCharsetBudget({
            outDir: ctx.outDir,
            routes: ctx.routes,
            mode: ctx.charsetMode,
            debug,
            sink: (s) => Object.assign(report.p12 as object, s),
          })

          // P2 — every published URL resolves to what it promises. Runs LAST
          // because it is the only rail that can touch the network.
          await runEmittedUrlHonesty({
            outDir: ctx.outDir,
            projectRoot: ctx.projectRoot,
            routes: ctx.routes,
            siteUrl: ctx.siteUrl,
            base: ctx.base,
            mode: ctx.urlMode,
            allow: ctx.urlAllow,
            network: ctx.urlNetwork,
            debug,
            sink: (a, v) => {
              const p2 = report.p2 as Record<string, unknown>
              p2.audit = a
              p2.violations = v
            },
          })
        } finally {
          if (reportPath) {
            try {
              fs.mkdirSync(path.dirname(path.resolve(reportPath)), { recursive: true })
              fs.writeFileSync(path.resolve(reportPath), JSON.stringify(report, null, 2), 'utf8')
              console.log(`[dcs-seo] honesty report written: ${path.resolve(reportPath)}`)
            } catch (err) {
              console.warn('[dcs-seo] could not write honesty report:', err)
            }
          }
        }
      }
    },

    /**
     * Dev-only: inject the FRESH on-disk `seo.yaml` as an inline
     * `globalThis.__DCS_SEO__ = {…}` classic script at the top of `<head>`,
     * mirroring dcsContentPlugin. Lets the runtime `useSEO` SPA path reflect
     * on-disk SEO edits on the portal's `?_hc` hard reload with no restart.
     * Gated on `isDev` so it never runs in `vite`/`vitepress build` (the build
     * path keeps the baked `define` literal + the `writeBundle` emitter).
     *
     * `__DCS_PAGES__` (the `installSeoHead` route manifest) rides the same
     * refresh. It is injected INDEPENDENTLY of seo.yaml: a site can have a
     * pages.yaml and no seo.yaml, and returning early on a missing seo.yaml
     * would leave the router hook manifest-less (and therefore inert) in dev
     * only — a dev/prod behaviour split is exactly the kind of divergence this
     * plan exists to stop.
     */
    transformIndexHtml: {
      order: 'pre',
      handler(html) {
        if (!isDev) return html

        const projectRoot = resolvedConfig?.root || process.cwd()
        const tags: HtmlTagDescriptor[] = []

        const config = readDcsYamlFresh<SeoConfiguration>(projectRoot, seoPath, 'dcs-seo', debug)
        // No file (or unparseable) ⇒ leave the global unset; the consumer's
        // `typeof globalThis.__DCS_SEO__` check falls back to defaults.
        if (config !== undefined) {
          if (debug) {
            console.log('[dcs-seo] serve: injecting fresh __DCS_SEO__ into <head>')
          }
          tags.push({
            tag: 'script',
            children: buildGlobalAssignScript('__DCS_SEO__', config),
            injectTo: 'head-prepend',
          })
        }

        const routes = loadPagesManifest(projectRoot, pagesPath, debug)
        if (routes) {
          // SAME exclusion filter as the build define above. A dev-only manifest
          // that carried excluded routes would make `installSeoHead` behave
          // differently in dev than in prod — the dev/prod split this whole
          // inject exists to avoid.
          const excludedGlobs = loadExcludedGlobs(projectRoot, pagesPath)
          const emitted = routes.filter((r) => isRouteEmitted(r, { exclude, excludedGlobs }))
          // Dev REPORTS the colliding manifest; the BUILD is the gate that
          // refuses it. Throwing from `transformIndexHtml` would replace a
          // precise message with a blank dev-server page.
          const dupes = findDuplicateNormalizedPaths(emitted)
          if (dupes.length > 0) {
            console.error(
              `[dcs-seo] serve: ${formatDuplicateNormalizedPaths(dupes)}\n` +
                `[dcs-seo] serve: this is reported here and FAILS the production build.`
            )
          }
          if (debug) {
            console.log(
              `[dcs-seo] serve: injecting fresh __DCS_PAGES__ (${emitted.length} of ` +
                `${routes.length} route(s), excluded ones omitted) into <head>`
            )
          }
          tags.push({
            tag: 'script',
            children: buildGlobalAssignScript('__DCS_PAGES__', {
              routes: emitted.map((r) => ({
                slug: r.slug,
                path: r.path,
                ...(r.title ? { title: r.title } : {}),
              })),
              noindex,
            }),
            injectTo: 'head-prepend',
          })
        }

        if (tags.length === 0) return html
        return { html, tags }
      },
    },

    // Watch for changes in development.
    //
    // Downgraded from `server.restart()` to a debounced client `full-reload`:
    // freshness already comes from `transformIndexHtml` re-reading the YAML on
    // every HTML request, so all we need on change is a browser reload. A
    // `server.restart()` would rebuild VitePress's dev MiniSearch index and can
    // throw "server restart failed" on a duplicate heading id — the fragility
    // this fix removes. A `full-reload` does neither.
    configureServer(server) {
      const projectRoot = resolvedConfig?.root || process.cwd()

      const watchPaths = [
        path.resolve(projectRoot, seoPath),
        path.resolve(projectRoot, '..', seoPath),
      ].map((p) => path.normalize(p))

      watchPaths.forEach((watchPath) => {
        if (fs.existsSync(watchPath)) {
          server.watcher.add(watchPath)
        }
      })

      let reloadTimer: ReturnType<typeof setTimeout> | undefined
      server.watcher.on('change', (changedPath) => {
        if (!watchPaths.includes(path.normalize(changedPath))) return
        if (reloadTimer) clearTimeout(reloadTimer)
        reloadTimer = setTimeout(() => {
          if (debug) {
            console.log('[dcs-seo] seo.yaml changed, sending full-reload')
          }
          server.ws.send({ type: 'full-reload' })
        }, 100)
      })
    },
  }
}
